import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Mail, Lock, ArrowRight, Shield, CheckCircle, Users, Briefcase } from 'lucide-react';
import { useAuthStore } from '@/store/authStore';
import { useGlobalToast } from '@/contexts/ToastContext';
import { useFormValidation, validation } from '@/utils/validation';
import { rateLimiter, getUserIdentifier } from '@/utils/rateLimiter';
import type { LoginForm } from '@/types';
import { FormInput } from '@/components/auth/FormInput';

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isLoading } = useAuthStore();
  const { success, error } = useGlobalToast();
  const [loginError, setLoginError] = useState<string | null>(null);

  const roleFromState = location.state?.role;

  const { data, errors, touched, updateField, touchField, validateAll } =
    useFormValidation<LoginForm>(
      { email: '', password: '' },
      {
        email: validation.email,
        password: (v: string) => v ? null : 'Password is required',
      }
    );

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginError(null);

    const identifier = getUserIdentifier();
    if (!rateLimiter.isAllowed('login', identifier)) {
      const blockedUntil = rateLimiter.getBlockedUntil('login', identifier);
      const msg = blockedUntil
        ? `Too many login attempts. Try again at ${new Date(blockedUntil).toLocaleTimeString()}`
        : 'Too many login attempts.';
      error('Rate Limited', msg);
      setLoginError(msg);
      return;
    }

    if (!validateAll()) {
      error('Validation Error', 'Please check your email and password');
      return;
    }

    try {
      await login(data);
      const returnTo = location.state?.returnTo;
      const returnState = location.state?.returnState;
      const message = location.state?.message;

      if (returnTo && returnTo === '/create-job' && returnState) {
        success('Login successful', message || 'Welcome back! Continuing with your job posting...');
        navigate(returnTo, { state: { from: 'login', returnState }, replace: true });
      } else {
        success('Login successful', 'Redirecting to your dashboard...');
        navigate('/dashboard', { replace: true });
      }
    } catch (err: any) {
      const msg = err?.message?.includes('Invalid login credentials')
        ? 'The email or password you entered is incorrect.'
        : err?.message || 'Invalid credentials';
      setLoginError(msg);
      error('Sign In Failed', msg);
    }
  };

  return (
    <div className="min-h-screen bg-white flex">
      <a href="#login-form" className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50 focus:px-4 focus:py-2 focus:bg-primary-600 focus:text-white focus:rounded-lg">
        Skip to login form
      </a>

      {/* ── Left panel (desktop) ── */}
      <div className="hidden lg:flex lg:w-96 xl:w-[420px] bg-primary-600 flex-col p-10 relative overflow-hidden flex-shrink-0">
        <div className="absolute inset-0 opacity-[0.07]">
          <div className="absolute -top-20 -right-20 w-72 h-72 bg-white rounded-full" />
          <div className="absolute -bottom-32 -left-16 w-96 h-96 bg-white rounded-full" />
        </div>

        <div className="relative z-10 flex items-center gap-2">
          <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
            <Briefcase className="w-4 h-4 text-white" />
          </div>
          <span className="text-white font-bold text-sm tracking-wide">RapidFix</span>
        </div>

        <div className="relative z-10 mt-16 space-y-6">
          <div>
            <h1 className="text-3xl font-bold text-white leading-tight">Welcome back</h1>
            <p className="text-primary-200 text-sm mt-3 leading-relaxed">Sign in to manage your jobs, quotes, and connect with professionals.</p>
          </div>
          <div className="space-y-3 pt-2">
            {[
              { icon: Shield, text: 'Secure & encrypted login' },
              { icon: CheckCircle, text: 'Trusted by thousands' },
              { icon: Users, text: 'Active professional community' },
            ].map(({ icon: Icon, text }) => (
              <div key={text} className="flex items-center gap-3">
                <div className="w-7 h-7 bg-white/15 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Icon className="w-3.5 h-3.5 text-white" />
                </div>
                <span className="text-sm text-primary-100">{text}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="relative z-10 mt-auto flex items-center gap-2 text-xs text-primary-200">
          <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
          Secure & encrypted connection
        </div>
      </div>

      {/* ── Right panel (form) ── */}
      <div className="flex-1 overflow-y-auto">
        <div className="min-h-full flex flex-col px-5 sm:px-10 lg:px-14 xl:px-20 pt-8 pb-10">

          {/* Mobile logo */}
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <div className="w-7 h-7 bg-primary-600 rounded-lg flex items-center justify-center">
              <Briefcase className="w-3.5 h-3.5 text-white" />
            </div>
            <span className="text-gray-900 font-bold text-sm">RapidFix</span>
          </div>

          <div className="w-full max-w-md my-auto">
            {/* Heading */}
            <div className="mb-7">
              <h2 className="text-2xl font-bold text-gray-900">Sign in to your account</h2>
              <p className="text-sm text-gray-500 mt-1">
                Don't have an account?{' '}
                <button type="button" onClick={() => navigate('/signup')} className="text-primary-600 hover:text-primary-700 font-medium focus:outline-none focus:underline">
                  Create one free
                </button>
              </p>
            </div>

            {roleFromState && (
              <div className="mb-5 inline-flex items-center gap-2 px-3 py-1.5 bg-primary-50 border border-primary-200 text-xs font-medium text-primary-700 rounded-lg">
                <div className="w-1.5 h-1.5 rounded-full bg-primary-600" />
                Signing in as {roleFromState}
              </div>
            )}

            <form id="login-form" className="space-y-4" onSubmit={onSubmit} noValidate>
              {loginError && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-sm text-red-700">{loginError}</p>
                </div>
              )}

              <FormInput label="Email Address" value={data.email} onChange={(v: string) => updateField('email', v)} onBlur={() => touchField('email')} placeholder="you@example.com" icon={Mail} type="email" error={touched.email ? errors.email : ''} required autoFocus showSuccess={!!data.email && !errors.email} />

              <FormInput label="Password" value={data.password} onChange={(v: string) => updateField('password', v)} onBlur={() => touchField('password')} placeholder="Enter your password" icon={Lock} error={touched.password ? errors.password : ''} required isPassword ariaLabel="Password" />

              <div className="flex justify-end">
                <Link to="/forgot-password" className="text-sm text-primary-600 hover:text-primary-700 font-medium focus:outline-none focus:underline">
                  Forgot password?
                </Link>
              </div>

              <button type="submit" disabled={isLoading} className="w-full py-2.5 bg-primary-600 text-white font-semibold text-sm rounded-lg hover:bg-primary-700 focus:ring-4 focus:ring-primary-500 focus:ring-offset-2 transition-all flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed">
                {isLoading ? (
                  <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />Signing In...</>
                ) : (
                  <>Sign In<ArrowRight className="w-4 h-4 ml-2" /></>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
